import { authFetch, fetchWithAuth, BASE_BACKEND_URL } from '../../auth'
import React, { useEffect, useState, useRef, useContext } from 'react'
import Pagination from '../../layout/Pagination'
import dayjs from '../../utils/dayjsConfig'
import { DEFAULT_PER_PAGE } from '../../constants/constant'
import usePagination from '../../layout/usePagination'
import LoadingScreen from '../../components/LoadingScreen'

function AssetIndexView({
  title,
  endpoint,
  FiltersComponent,
  TableComponent,
  showModelPath,
  navActiveItem,
  processFiltersFn,
  statusConstants,
  searchPlaceholder
}) {
  const [isFetching, setIsFetching] = useState(true)
  const [query, setQuery] = useState('')
  const [totalPages, setTotalPages] = useState(0)
  const [data, setData] = useState([])
  const [filtersfetch, setFilterFetch] = useState([])
  const [filters, setFilters] = useState([])
  const [resetReactFilters, setResetReactFilters] = useState(false)
  const [labelToIdMapping, setLabelToIdMapping] = useState({})
  const [allSelectedOptions, setAllSelectedOptions] = useState(new Map())
  const [filterQuery, setFilterQuery] = useState('')
  const [dateQuery, setDateQuery] = useState(null)
  const [selectedDates, setSelectedDates] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE)
  const filtersDisabled = !filtersfetch || filtersfetch.length === 0
  const { pagination, setPagination, handlePaginationClick } = usePagination({ per_page: perPage })

  const abortControllerRef = useRef(null)

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort('Fetch aborted before unload.')
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  const fetchFilters = async () => {
    try {
      const response = await fetchWithAuth(`${BASE_BACKEND_URL}/${endpoint}?client_names_only=true`, {
        method: 'GET',
        signal: abortControllerRef.current.signal
      })

      if (!response) {
        setFilterFetch([])
        console.error('No response from the server.')
        setIsFetching(false)
        return
      }
      setFilterFetch(response.result)
    } catch (error) {
      console.error('Error fetching filters:', error)
      setFilterFetch([])
      setTotalPages(0)
    } finally {
      setIsFetching(false)
    }
  }

  const fetchDataFromServer = async () => {
    // Cancelamos el fetch previo antes de crear uno nuevo
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    abortControllerRef.current = new AbortController()

    try {
      const response = await authFetch(
        `${BASE_BACKEND_URL}/${endpoint}?page=${pagination.current_page}&per_page=${pagination.per_page}${
          filterQuery || dateQuery || query ? '&filter_search=true' : ''
        }${filterQuery || ''}${dateQuery || ''}${query ? `&q=${query}` : ''}`,
        {
          method: 'GET',
          signal: abortControllerRef.current.signal,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      )
      if (response.status !== 200) {
        setData([])
        setTotalPages(0)
      } else {
        const res = await response.json()
        setData(res.table ? res.table : res)
        setTotalPages(Math.ceil((res.count || res.length) / perPage))
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error:', error)
      }
    }
  }

  const handleSearch = (event) => {
    if (event.keyCode === 13) {
      event.preventDefault()
      const _query = event.target.value
      if (_query !== '') {
        setQuery(_query)
        fetchDataFromServer()
      }
    }
  }

  const showModel = (uuid) => {
    window.open(`${showModelPath}${uuid}`, '_blank')
  }
  useEffect(() => {
    if (filtersfetch) {
      const { filters: newFilters, mapping } = processFiltersFn(filtersfetch, statusConstants)
      setFilters(newFilters)
      setLabelToIdMapping(mapping)
    }
  }, [filtersfetch])

  const buildFiltersQuery = () => {
    const { FILTER_KEYS, LABEL_TO_STATUS_VALUE } = statusConstants
    let queryParts = []

    const userLabels = allSelectedOptions?.get(FILTER_KEYS.USER) || []
    const userUids = userLabels.map((label) => labelToIdMapping[label])
    if (userUids.length > 0) {
      queryParts.push(`user_uid=${encodeURIComponent(userUids.join(','))}`)
    }

    const clientLabels = allSelectedOptions?.get(FILTER_KEYS.CLIENT) || []
    const clientUids = clientLabels.map((label) => labelToIdMapping[label])
    if (clientUids.length > 0) {
      queryParts.push(`client_uid=${encodeURIComponent(clientUids.join(','))}`)
    }

    const statusLabels = allSelectedOptions?.get(FILTER_KEYS.STATUS) || []
    const statusValues = statusLabels.map((label) => LABEL_TO_STATUS_VALUE[label] || label)
    if (statusValues.length > 0) {
      queryParts.push(`status=${encodeURIComponent(statusValues.join(','))}`)
    }

    const queryParam = queryParts.length > 0 ? `&${queryParts.join('&')}` : ''
    setFilterQuery(queryParam)
    setPagination((prev) => ({ ...prev, current_page: 1 }))
  }

  useEffect(() => {
    if (allSelectedOptions) {
      buildFiltersQuery()
    }
  }, [allSelectedOptions])

  const resetFilters = () => {
    setAllSelectedOptions(new Map())
    setFilterQuery('')
    setDateQuery(null)
    setSelectedDates(null)
    setSearchQuery('')
    setQuery('')
    setPagination((prev) => ({ ...prev, current_page: 1 }))
    setResetReactFilters(!resetReactFilters)
  }

  const handleDateFilter = (datesObj) => {
    let queryParam = ''
    let startDate, endDate

    if (!datesObj || datesObj.length === 0) {
      setDateQuery(null)
      setSelectedDates(null)
      setPagination((prev) => ({ ...prev, current_page: 1 }))
    } else {
      setSelectedDates(datesObj)
      let formattedDates = datesObj.map((date) => dayjs.unix(date.unix))

      if (formattedDates.length > 1) {
        startDate = dayjs.min(formattedDates)
        endDate = dayjs.max(formattedDates)
      } else {
        startDate = formattedDates[0]
        endDate = formattedDates[0]
      }

      queryParam = `&start_date=${startDate.toISOString()}&end_date=${endDate.toISOString()}`
      setDateQuery(queryParam)
      setPagination((prev) => ({ ...prev, current_page: 1 }))
    }
  }

  useEffect(() => {
    fetchDataFromServer()
  }, [query, filterQuery, dateQuery, pagination.current_page])

  useEffect(() => {
    fetchFilters()
  }, [])

  return (
    <div>
      <div>
        <div>
          {isFetching && <LoadingScreen text="Cargando..." />}
          {!isFetching && (
            <>
              <div className="index-header detailed-container">
                <h2>{title}</h2>
              </div>
              <div className="index-table">
                <div className="show-area">
                  <FiltersComponent
                    filters={filters}
                    resetReactFilters={resetReactFilters}
                    filtersDisabled={filtersDisabled}
                    allSelectedOptions={allSelectedOptions}
                    setAllSelectedOptions={setAllSelectedOptions}
                    labelToIdMapping={labelToIdMapping}
                    selectedDates={selectedDates}
                    handleDateFilter={handleDateFilter}
                    query={query}
                    setQuery={setQuery}
                    fetchData={fetchDataFromServer}
                    searchQuery={searchQuery}
                    filterQuery={filterQuery}
                    dateQuery={dateQuery}
                    resetFilters={resetFilters}
                    searchPlaceholder={searchPlaceholder}
                  />
                  <TableComponent data={data} showModel={showModel} />
                </div>
              </div>
              {totalPages > 1 && (
                <nav className="index-table">
                  <Pagination
                    current_page={pagination.current_page}
                    total_pages={totalPages}
                    handle_pagination={(e) => handlePaginationClick(e, totalPages)}
                  />
                </nav>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default AssetIndexView
